import React, { useMemo, useRef, useState } from 'react';

import { get, isEmpty, map, min } from 'lodash';
import { useStandardsList } from 'sdk';

import { ReactComponent as SearchIcon } from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/assets/search.svg';
import { ReactComponent as StandardIcon } from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/assets/standard.svg';
import { getOptionLabel } from 'utils/assignments';

import styles from './styles.module.scss';

const StandardSelector = ({ selectedStandards, onAdd, onRemove }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef();

  const { data, loading: isLoading } = useStandardsList({
    params: { retrieve_root_standards: false, math_standards: true, limit: 100 }
  });

  const rawStandards = get(data, 'results', []);

  const filteredOptions = useMemo(() => {
    const selectedIds = new Set(map(selectedStandards, 'id'));

    const available = rawStandards.filter(
      (standard) => !selectedIds.has(standard.id)
    );

    if (isEmpty(searchTerm)) return available;
    const term = searchTerm.toLowerCase();
    return available.filter(
      (standard) =>
        standard.code.toLowerCase().includes(term) ||
        standard.short_description.toLowerCase().includes(term)
    );
  }, [rawStandards, searchTerm, selectedStandards]);

  const minLevel = useMemo(
    () => min(map(filteredOptions, 'level')) || 0,
    [filteredOptions]
  );

  const handleStandardSelect = ({ standard }) => {
    onAdd({ standard: standard });
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div className={styles.fieldSection} ref={containerRef}>
      <div className={styles.fieldLabel}>
        <StandardIcon /> <span>Learning Standards</span>
      </div>

      <div className={styles.searchContainer}>
        <div className={styles.inputWrapper}>
          <input
            type="text"
            className={styles.inputField}
            placeholder="Search and add standards..."
            value={searchTerm}
            onFocus={() => setIsOpen(true)}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchIcon className={styles.searchIcon} />
        </div>

        {isOpen && !isEmpty(searchTerm) && (
          <div className={styles.dropdown}>
            {isLoading ? (
              <div className={styles.message}>Loading...</div>
            ) : !isEmpty(filteredOptions) ? (
              map(filteredOptions, (standard) => {
                const paddingLeft = (standard.level - minLevel) * 16 + 12;
                return (
                  <div
                    key={standard.id}
                    className={styles.option}
                    style={{ paddingLeft: `${paddingLeft}px` }}
                    onClick={() => {
                      handleStandardSelect({ standard: standard });
                    }}
                  >
                    <span className={styles.name}>
                      {getOptionLabel({ option: standard })}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className={styles.message}>No matches found</div>
            )}
          </div>
        )}
      </div>

      {!isEmpty(selectedStandards) && (
        <div className={styles.chipsContainer}>
          {selectedStandards.map((standard) => (
            <div key={standard.id} className={styles.standardChip}>
              <span className={styles.chipText}>{standard.code}</span>
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => onRemove({ id: standard.id })}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StandardSelector;
